'use client';

import debounce from 'lodash/debounce';
import { Check, MessageSquare, Search, Share2, Sparkles, Users } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { fetchContacts } from '@/core/apis/content-apis';
import { appSettings } from '@/settings/app-settings';

import { ContentRegistry } from '../registries';
import { ContentItemProps } from '../registries/ContentRegistry';

interface ShareRecipient {
  _id: string;
  name: string;
  email: string;
  destinationId: string;
  displayPic?: string;
  handle: string;
}

const ShareSuggestionsCard: React.FC<ContentItemProps> = ({
  content,
  variant = 'card',
  onAction,
  className,
}) => {
  const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(new Set());
  const [sharedRecipients, setSharedRecipients] = useState<Set<string>>(new Set());
  const [isSharing, setIsSharing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ShareRecipient[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [showMessageInput, setShowMessageInput] = useState(false);
  const [expandedView, setExpandedView] = useState(false);

  // Get recipients from content data
  const recipients: ShareRecipient[] = content.data?.recipients || [];

  const sharedContent = content.data?.sharedContent || {
    title: content.title,
    type: content.type,
    preview: content.media?.thumbnail?.url || content.description,
  };

  // Debounced search function
  const searchContacts = useCallback(
    debounce(async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await fetchContacts(appSettings.api, {
          limit: 9,
          offset: 0,
          belongsTo: 'USER',
          search: query,
        });

        // Filter out already suggested recipients and format results
        const formattedResults = (response.contacts || [])
          .map((contact: any) => ({
            ...contact,
            handle: contact.handle || contact.email?.split('@')[0] || 'user',
          }))
          .filter(
            (contact: ShareRecipient) => !recipients.some((r) => r.destinationId === contact.destinationId),
          );

        setSearchResults(formattedResults);
      } catch (error) {
        console.error('Contact search failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [recipients],
  );

  useEffect(() => {
    searchContacts(searchQuery);
  }, [searchQuery, searchContacts]);

  const handleRecipientToggle = (destinationId: string) => {
    const newSelected = new Set(selectedRecipients);
    if (newSelected.has(destinationId)) {
      newSelected.delete(destinationId);
    } else {
      newSelected.add(destinationId);
    }
    setSelectedRecipients(newSelected);
  };

  const handleShare = async () => {
    if (selectedRecipients.size === 0) return;

    setIsSharing(true);

    try {
      // Prepare share data matching backend API structure
      const shareData = {
        userIds: Array.from(selectedRecipients),
        contentData: {
          contentType: 'link',
          url: [content.data?.href || window.location.href],
        },
        ...(shareMessage.trim() && { message: shareMessage.trim() }),
      };

      // Trigger the share action
      await onAction?.('share', content, shareData);

      // Update shared recipients
      const newShared = new Set(sharedRecipients);
      selectedRecipients.forEach((id) => newShared.add(id));
      setSharedRecipients(newShared);
      setSelectedRecipients(new Set());
      setShareMessage('');
      setShowMessageInput(false);
      setShowSearch(false);
      setSearchQuery('');

      // Show success feedback
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      console.error('Share failed:', error);
    } finally {
      setIsSharing(false);
    }
  };

  const getAvatarUrl = (recipient: ShareRecipient) => {
    if (recipient.displayPic && recipient.displayPic !== 'placeholder-image.jpeg') {
      return `${appSettings.api.S3_MEDIA_COMPRESSED}${recipient.displayPic}`;
    }
    // Use name, handle, or email for avatar generation
    const displayName = recipient.name || recipient.handle || recipient.email?.split('@')[0] || 'User';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=7C3AED&color=fff`;
  };

  // Combine all recipients (suggested + search results)
  const allRecipients = [...recipients, ...searchResults];

  if (variant === 'minimal') {
    return (
      <div
        className={`inspect-theme-bg-elevated rounded-lg p-3 cursor-pointer hover:inspect-theme-shadow transition-all ${className}`}
        onClick={() => onAction?.('inspect', content)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span className="text-sm font-medium inspect-theme-text">Share suggestions</span>
          </div>
          <Users className="w-4 h-4 inspect-theme-text-muted" />
        </div>
      </div>
    );
  }

  // Don't render if no recipients and no search capability
  if (recipients.length === 0 && !showSearch) {
    return null;
  }

  const displayedRecipients = expandedView ? allRecipients : allRecipients.slice(0, 6);
  const hasMoreRecipients = allRecipients.length > 6;

  return (
    <article className={`inspect-theme-bg-elevated rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-5 border-b inspect-theme-border">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold inspect-theme-text">AI found perfect matches</h3>
              <p className="text-sm inspect-theme-text-muted">Share with people who&apos;ll love this</p>
            </div>
          </div>
          {/* Search toggle */}
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Search contacts"
          >
            <Search className="w-4 h-4 inspect-theme-text-muted" />
          </button>
        </div>

        {/* Search Input */}
        {showSearch && (
          <div className="mt-3 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search contacts by name or email..."
              className="w-full px-4 py-2 pl-10 rounded-lg border inspect-theme-border bg-white dark:bg-gray-800 inspect-theme-text focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 inspect-theme-text-muted" />
            {isSearching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            )}
          </div>
        )}

        {/* Content Preview */}
        {sharedContent && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="flex items-center gap-3">
              {sharedContent.preview &&
              typeof sharedContent.preview === 'string' &&
              sharedContent.preview.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <img
                  src={sharedContent.preview}
                  alt={sharedContent.title}
                  className="w-12 h-12 rounded object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-gray-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium inspect-theme-text truncate">{sharedContent.title}</p>
                <p className="text-xs inspect-theme-text-muted capitalize">{sharedContent.type}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recipients List */}
      <div className="p-5 space-y-3 max-h-[400px] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium inspect-theme-text">
            {searchQuery ? 'Search results' : 'Suggested recipients'}
          </span>
          <span className="text-xs inspect-theme-text-muted">{selectedRecipients.size} selected</span>
        </div>

        {displayedRecipients.map((recipient) => {
          const isSelected = selectedRecipients.has(recipient.destinationId);
          const isShared = sharedRecipients.has(recipient.destinationId);
          const isSearchResult = searchResults.some((r) => r.destinationId === recipient.destinationId);

          return (
            <div
              key={recipient._id}
              className={`flex items-center gap-3 p-3 rounded-lg transition-all cursor-pointer
                ${
                  isShared
                    ? 'bg-green-50 dark:bg-green-900/20 opacity-75'
                    : isSelected
                    ? 'bg-purple-50 dark:bg-purple-900/20 inspect-theme-shadow'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }`}
              onClick={() => !isShared && handleRecipientToggle(recipient.destinationId)}
            >
              <div className="relative">
                <img
                  src={getAvatarUrl(recipient)}
                  alt={recipient.name || recipient.handle || 'User'}
                  className="w-10 h-10 rounded-full object-cover"
                  onError={(e) => {
                    const displayName =
                      recipient.name || recipient.handle || recipient.email?.split('@')[0] || 'User';
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      displayName,
                    )}&background=7C3AED&color=fff`;
                  }}
                />
                {(isSelected || isShared) && (
                  <div
                    className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center
                    ${isShared ? 'bg-green-500' : 'bg-purple-500'}`}
                  >
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-medium inspect-theme-text text-sm">
                  {recipient.name || recipient.handle || recipient.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-xs inspect-theme-text-muted truncate">
                  {recipient.email || `@${recipient.handle}`}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {isSearchResult && (
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-1 rounded">
                    From search
                  </span>
                )}
                {isShared && (
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">Shared</span>
                )}
              </div>
            </div>
          );
        })}

        {/* Show more/less button */}
        {hasMoreRecipients && !searchQuery && (
          <button
            onClick={() => setExpandedView(!expandedView)}
            className="w-full py-2 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
          >
            {expandedView ? 'Show less' : `Show ${allRecipients.length - 6} more`}
          </button>
        )}

        {/* No results message */}
        {searchQuery && searchResults.length === 0 && !isSearching && (
          <div className="text-center py-4 text-sm inspect-theme-text-muted">
            No contacts found matching &ldquo;{searchQuery}&rdquo;
          </div>
        )}
      </div>

      {/* Message Input Section */}
      <div className="px-5 pb-2">
        {!showMessageInput ? (
          <button
            onClick={() => setShowMessageInput(true)}
            className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            Add a message
          </button>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium inspect-theme-text">
                Add a personal message (optional)
              </span>
              <button
                onClick={() => {
                  setShowMessageInput(false);
                  setShareMessage('');
                }}
                className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Cancel
              </button>
            </div>
            <textarea
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              placeholder="Write a message to share with this content..."
              className="w-full px-3 py-2 rounded-lg border inspect-theme-border bg-white dark:bg-gray-800 inspect-theme-text focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              rows={3}
            />
          </div>
        )}
      </div>

      {/* Action Button */}
      <div className="p-5 pt-3">
        <button
          onClick={handleShare}
          disabled={selectedRecipients.size === 0 || isSharing}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2
            ${
              selectedRecipients.size === 0
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                : showSuccess
                ? 'bg-green-500 text-white'
                : 'inspect-theme-button-primary hover:inspect-theme-shadow'
            }`}
        >
          {showSuccess ? (
            <>
              <Check className="w-5 h-5" />
              Shared successfully!
            </>
          ) : isSharing ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Sharing...
            </>
          ) : (
            <>
              <Share2 className="w-5 h-5" />
              Share with {selectedRecipients.size || 'selected'}{' '}
              {selectedRecipients.size === 1 ? 'person' : 'people'}
            </>
          )}
        </button>

        {/* AI Learning Note */}
        <p className="text-xs text-center mt-3 inspect-theme-text-muted">
          Your AI learns from your sharing patterns to suggest better matches
        </p>
      </div>
    </article>
  );
};

// Auto-register with ContentRegistry
ContentRegistry.register('share-suggestions', ShareSuggestionsCard);

export default ShareSuggestionsCard;
